// src/padplanner/DashboardPage.jsx
//
// PadPlanner's landing (/padplanner), mockup 1.
//
// ── WHAT IS HERE ───────────────────────────────────────────────────────────
//
//   Plans in flight   active minus issued, the mockup's arithmetic, with the
//                     sentence under it and the stacked status bar; the legend
//                     names all five statuses with their counts
//   Key figures       one cell: plans starting in the next 30 days
//   Needs your action the server's needs_action (S6a1) for this user: steps
//                     waiting for their decision, plans sent back to them,
//                     and, for an org_admin, approved plans to issue; for an
//                     editor, cases a landowner returned (S6b2)
//   Plans on the map  one pin per plan, colored by status, on Mapbox dark;
//                     each says which plan it is (PlanPinsOverlay.js: a
//                     tooltip on hover, focus or tap, and the plan number
//                     beside it from zoom 10)
//   All plans         every status as a tab with its count, a search, type
//                     and owner filters, keyset-paged ("Showing N of M")
//
// ── CUT, MEANING NOT RENDERED AT ALL ───────────────────────────────────────
//
// No zero, no placeholder, no "coming soon": a zero for a thing that cannot
// happen reads as data.
//   S6  "Waiting on me", the approvals column and bars, the in-review median,
//       the 30-day approved/pending split, and the "In review" key figure
//       (DB's decision 1; S6a2 built only "Needs your action")
//   S5  layout warnings      S8  "Import Visio"
//   --  the Operating AOI selector and "across N AOIs" (a plan has no AOI),
//       "as-built" (not a status), "Page 1 of 8" (keyset paging cannot jump)
//
// ── THE LAYOUT (DB, 2026-10-30; departs from mockup 1) ──────────────────────
//
// Two rows of two panels; each row stacks to one column under 1100 px.
//
//   row 1   Plans in flight | Needs your action, ROW1_COLS. The row is In
//           flight's height, but never less than NEEDS_MIN_H: Needs your
//           action fills its cell (absolute, inset 0) and scrolls inside when
//           its rows are taller; In flight stretches to the row.
//   row 2   Plans on the map | All plans, 50/50. The map's panel is square
//           (aspect-ratio 1/1 of its column); All plans fills the same height
//           and scrolls inside.
//
// Stacked, every panel is its own content's height, and the map stays
// square at full width. MapLibre follows its container (trackResize's
// ResizeObserver); the pins' one fit is redone at the new size until the
// user moves the map (PadPlannerMap.jsx).
//
// ── THE PROJECT ────────────────────────────────────────────────────────────
//
// The TopNav picker's project goes to the server on every call
// (x-aiprime-project) and the server filters by it (148). So the page only
// asks again when the picker changes, and says which project it shows
// (ProjectScope: the project, the plans it leaves out, "Show all projects"),
// so an empty project is never read as an empty organization.
//
// ── A FAILED READ IS SAID ──────────────────────────────────────────────────
//
// The summary, the map's pins and the plans table each say their own failure
// with a Retry (ReadFailed), never zero plans. An answer is kept per project
// and attempt, so a failure never leaves another project's figures showing.
//
// The New plan button shows only to someone who can create one
// (can_edit_site_plans, which the dashboard answer carries).
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { LandPlot, Plus } from "lucide-react";
import PadPlannerGate from "./PadPlannerGate";
import PadPlannerMap from "./PadPlannerMap";
import StatusPill from "./StatusPill";
import { SectionLabel } from "./SidePanel";
import { getDashboard, getPerimeters, listPlans } from "../api/padplannerApi";
import { useProjectName } from "./useProjectName";
import ProjectScope from "./ProjectScope";
import ReadFailed from "./ReadFailed";
import {
  PLAN_STATUSES, STATUS_LABELS, PLAN_TYPES, PLAN_TYPE_LABELS, formatDate, formatUpdated, imageryLabel,
} from "./planVocabulary";
import { STATUS_STYLE } from "./planStatus";
import { filtersFor, countForTab } from "./planFilters";
import { WORLD_BBOX, pinsFromPerimeters } from "./planMapData";
import { ppDarkStyle } from "./ppMapStyle";
import PlanPinsOverlay from "./PlanPinsOverlay";
import { needsActionWhat, readyWhat } from "./landowner";
import { PADPLANNER_DISPLAY_NAME } from "../constants/analyticsProducts";

export default function DashboardPage() {
  return (
    <PadPlannerGate section="dashboard">
      <Dashboard />
    </PadPlannerGate>
  );
}

// Row 1's columns at 1100 px and wider: In flight a fixed 620 px, Needs your
// action the rest. 620 is the status legend on one line: its natural width
// in IBM Plex Sans with every count at three digits is 581 px (501 at one
// digit), measured in the HUD harness, plus the panel's 34 px of padding and
// border, plus 5 px. At 440 px "Issued" fell to a line of its own (DB,
// 2026-10-30). A fixed column keeps the row's height the same at every
// window width; a ratio cannot.
export const ROW1_COLS = "min-[1100px]:grid-cols-[620px_minmax(0,1fr)]";
// Needs your action's cell at 1100 px and wider: the height it rendered at
// beside the 440 px In flight, which showed a working day (the HUD fixture:
// two decisions, one sent back, one to issue) without scrolling. At 620 px
// In flight is 244 px, and the row would shrink Needs your action with it.
const NEEDS_MIN_H = "min-[1100px]:min-h-[286.5px]";
// The legend: all five on one line when the panel's body is 581 px or wider.
// Narrower (a phone), it wraps as a whole: an aligned grid of three columns
// (three, then two), and under that one column. Never two columns, which
// leaves one item on a line of its own. The breakpoints are the items'
// widths with three-digit counts (72, 95, 154, 99 and 81 px; gaps 20):
// three columns need 388 px.
const LEGEND = "grid grid-cols-1 gap-x-5 gap-y-1 [@container(min-width:388px)]:grid-cols-[repeat(3,max-content)] [@container(min-width:581px)]:grid-cols-[repeat(5,max-content)]";
const TWO_UP = "grid grid-cols-1 items-stretch gap-5";
// A panel that fills its row's cell beside a taller neighbor, and scrolls inside.
const FILL_CELL = "min-[1100px]:absolute min-[1100px]:inset-0";

const TABLE_TABS = [{ key: "all", label: "All" }, ...PLAN_STATUSES.map((s) => ({ key: s, label: STATUS_LABELS[s] })), { key: "archived", label: "Archived" }];
const PAGE = 25;

function Dashboard() {
  const navigate = useNavigate();
  const { projectKey, projectName, showAllProjects } = useProjectName();

  const [dashAttempt, setDashAttempt] = useState(0);
  const dashKey = `${projectKey}:${dashAttempt}`;
  const [dash, setDash] = useState({ key: null, data: null, error: null });
  useEffect(() => {
    let cancelled = false;
    getDashboard()
      .then((d) => { if (!cancelled) setDash({ key: dashKey, data: d, error: null }); })
      .catch((e) => { if (!cancelled) setDash({ key: dashKey, data: null, error: e }); });
    return () => { cancelled = true; };
  }, [dashKey]);
  const shownDash = dash.key === dashKey ? dash : { data: null, error: null };
  const data = shownDash.data;
  const error = shownDash.error;

  const [pinsAttempt, setPinsAttempt] = useState(0);
  const pinsKey = `${projectKey}:${pinsAttempt}`;
  const [pinsState, setPinsState] = useState({ key: null, pins: null, error: null });
  useEffect(() => {
    let cancelled = false;
    getPerimeters(WORLD_BBOX)
      .then((fc) => { if (!cancelled) setPinsState({ key: pinsKey, pins: { fc: pinsFromPerimeters(fc), truncated: fc.truncated === true }, error: null }); })
      .catch((e) => { if (!cancelled) setPinsState({ key: pinsKey, pins: null, error: e }); });
    return () => { cancelled = true; };
  }, [pinsKey]);
  const pins = pinsState.key === pinsKey ? pinsState.pins : null;
  const pinsError = pinsState.key === pinsKey ? pinsState.error : null;

  const openPlan = useCallback((id) => navigate({ to: "/padplanner/plans/$planId", params: { planId: id } }), [navigate]);

  // The pins' tooltips and labels, over the map's circles.
  const [map, setMap] = useState(null);
  const overlayRef = useRef(null);
  const open = useRef(openPlan);
  useEffect(() => { open.current = openPlan; }, [openPlan]);
  useEffect(() => {
    if (!map) return undefined;
    const o = new PlanPinsOverlay({ map, onOpen: (id) => open.current(id) });
    overlayRef.current = o;
    return () => { o.destroy(); if (overlayRef.current === o) overlayRef.current = null; };
  }, [map]);
  useEffect(() => { overlayRef.current?.set(pins?.fc ?? null); }, [map, pins]);

  return (
    // h-full, not flex-1: the shell's <main> is a block box, so flex-1 here
    // did nothing and the page grew to its content, clipped by main's
    // overflow-hidden with nothing to scroll. h-full bounds the scroller;
    // overflow-auto scrolls it. The column inside has NO height: a flex
    // column with one shrinks its panels to fit, and a panel clips
    // (overflow-hidden), which hid the status bar and legend and boxed the
    // table into a one-row scroller. Unbounded, every panel is its content's
    // height and the page scrolls.
    <div className="h-full overflow-auto">
      <div className="flex flex-col gap-5 p-6" data-testid="pp-dashboard-column">
        <div className="flex items-start justify-between gap-6">
          <div className="flex min-w-0 flex-col gap-1.5">
            <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
              <div className="flex items-center gap-2.5">
                <LandPlot className="h-5 w-5 shrink-0 self-center text-accent-action" aria-hidden="true" />
                <h1 className="font-display text-[22px] font-semibold tracking-[-0.01em] text-fg-primary">{PADPLANNER_DISPLAY_NAME}</h1>
              </div>
            </div>
            <p className="text-[13px] leading-relaxed text-fg-secondary" data-testid="pp-subtitle">
              Pad and facility layouts — drafted on current imagery, reviewed, approved and shared
            </p>
          </div>
          {data?.can_edit && (
            <button type="button" className="op-btn op-btn--primary inline-flex shrink-0 items-center gap-1.5"
              onClick={() => navigate({ to: "/padplanner/new" })}>
              <Plus className="h-3.5 w-3.5" aria-hidden="true" />
              New plan
            </button>
          )}
        </div>

        <ProjectScope projectName={projectName} outside={data?.counts?.outside_project ?? null} onShowAll={showAllProjects} />

        {error && <ReadFailed what="the dashboard" error={error} onRetry={() => setDashAttempt((n) => n + 1)} testid="pp-dashboard-failed" />}

        {data && (
          <>
            <div className={`${TWO_UP} ${data.needs_action ? ROW1_COLS : ""}`} data-testid="pp-dash-row1">
              <InFlight counts={data.counts} scoped={Boolean(projectName)} onStatus={(s) => navigate({ to: "/padplanner/plans", search: { status: s } })} />
              {data.needs_action && (
                <div className={`relative min-w-0 ${NEEDS_MIN_H}`}>
                  <NeedsAction needs={data.needs_action} onOpen={openPlan} className={FILL_CELL} />
                </div>
              )}
            </div>

            <div className={`${TWO_UP} min-[1100px]:grid-cols-2`} data-testid="pp-dash-row2">
              <section className="op-panel aspect-square min-w-0" aria-labelledby="pp-map-head" data-testid="pp-map-panel">
                <div className="op-panel-head">
                  <h2 id="pp-map-head" className="op-panel-title">Plans on the map</h2>
                  <button type="button" className="op-meta hover:text-fg-primary" onClick={() => navigate({ to: "/padplanner/plans" })}>Open in Map</button>
                </div>
                <div className="relative min-h-0 flex-1">
                  <PadPlannerMap mapStyle={ppDarkStyle()} pins={pins?.fc ?? null} onPlanClick={openPlan} onMapReady={setMap}
                    hud={{ topRight: pinsError && (
                      <ReadFailed key="pins-failed" what="the plans on the map" error={pinsError} onRetry={() => setPinsAttempt((n) => n + 1)}
                        testid="pp-pins-failed" className="rounded-md border border-bg-border bg-bg/95 px-3 py-1.5 shadow" />
                    ) }} />
                </div>
                {pins?.truncated && <p className="op-meta px-3 py-1">Showing the 500 most recently updated plans.</p>}
              </section>
              <div className="relative min-w-0">
                <AllPlans counts={data.counts} projectKey={projectKey} onOpen={openPlan} className={FILL_CELL}
                  onSeeAll={(tab) => navigate({ to: "/padplanner/plans", search: tab === "all" ? {} : { status: tab } })} />
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

/* ── plans in flight ────────────────────────────────────────────────────── */

function InFlight({ counts, scoped, onStatus }) {
  const issued = counts.by_status.issued ?? 0;
  const inFlight = counts.active - issued;
  const plans = (n) => (n === 1 ? "1 plan is" : `${n} plans are`);
  return (
    <section className="op-panel min-w-0" aria-labelledby="pp-inflight-head" data-testid="pp-inflight-panel">
      <div className="op-panel-body gap-4 [container-type:inline-size]">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <div className="flex flex-col gap-1">
            <SectionLabel id="pp-inflight-head">Plans in flight</SectionLabel>
            <span className="op-figure text-[44px] leading-none text-fg-primary" data-testid="pp-inflight">{inFlight}</span>
            <p className="text-[13px] text-fg-secondary" data-testid="pp-inflight-sentence">
              {counts.active === 0
                ? (scoped ? "No plans in this project." : "No plans yet.")
                : `${plans(inFlight)} drafted or moving through review.${issued ? ` ${issued} more ${issued === 1 ? "is" : "are"} issued to the field.` : ""}`}
            </p>
          </div>
          <dl className="flex gap-6" data-testid="pp-key-figures">
            <div className="flex flex-col gap-1 border-l border-bg-border pl-4">
              <dt className="op-label uppercase">Starts · next 30 days</dt>
              <dd className="op-figure text-[26px] leading-none text-fg-primary" data-testid="pp-due-30d">{counts.due_30d}</dd>
            </div>
          </dl>
        </div>

        {/* The status bar and legend sit at the panel's bottom (mt-auto): when the
            row is taller than In flight's content (NEEDS_MIN_H), the space
            opens between the figures and the bar, not under the legend. */}
        <div className="mt-auto flex flex-col gap-4" data-testid="pp-inflight-foot">
          {counts.active > 0 && (
            <div className="flex h-2 w-full overflow-hidden rounded-full bg-bg-border" data-testid="pp-status-bar">
              {PLAN_STATUSES.filter((s) => counts.by_status[s] > 0).map((s) => (
                <div key={s} className={STATUS_STYLE[s].bg} data-status={s}
                  style={{ width: `${(100 * counts.by_status[s]) / counts.active}%` }}
                  title={`${STATUS_LABELS[s]}: ${counts.by_status[s]}`} />
              ))}
            </div>
          )}
          <ul className={LEGEND} aria-label="Plans by status" data-testid="pp-status-legend">
            {PLAN_STATUSES.map((s) => (
              <li key={s}>
                <button type="button" className={`flex items-center gap-1.5 text-[12.5px] ${STATUS_STYLE[s].text} hover:opacity-80`}
                  onClick={() => onStatus(s)}>
                  <span className="op-dot" aria-hidden="true" />
                  <span className="text-fg-secondary">{STATUS_LABELS[s]}</span>
                  <span className="op-mono text-fg-primary" data-testid={`pp-count-${s}`}>{counts.by_status[s]}</span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}

/* ── needs your action ─────────────────────────────────────────────────── */

export const NOTHING_NEEDS_ACTION_TEXT = "Nothing needs your action.";
const NEEDS_GROUPS = [
  { key: "approve", label: "Waiting for your decision", what: (r) => `Step ${r.step.ordinal} · ${r.step.role_label}` },
  { key: "revise", label: "Changes requested", what: (r) => `Sent back at step ${r.step.ordinal} · ${r.step.role_label}` },
  { key: "issue", label: "Approved, ready to issue", what: () => "Issue to the field" },
  // S6b2: a case the landowner returned. The row opens the plan, whose Landowner
  // section says the next step (a new revision, approved, resubmitted).
  { key: "landowner", label: "Landowner requested changes", what: needsActionWhat },
  // 158: a version the plan's intended landowner is owed, with no case of
  // theirs still open. The row opens the plan, whose Submit box preselects them.
  { key: "landowner_ready", label: "Ready to submit to landowner", what: readyWhat },
];

function NeedsAction({ needs, onOpen, className = "" }) {
  const groups = NEEDS_GROUPS.map((g) => ({ ...g, rows: needs[g.key] ?? [] })).filter((g) => g.rows.length);
  return (
    <section className={`op-panel ${className}`} aria-labelledby="pp-needs-head" data-testid="pp-needs-action">
      <div className="op-panel-head">
        <h2 id="pp-needs-head" className="op-panel-title">Needs your action</h2>
      </div>
      <div className="op-panel-body gap-3 overflow-y-auto" data-testid="pp-needs-scroll">
        {groups.length === 0 && <p className="op-meta" data-testid="pp-needs-none">{NOTHING_NEEDS_ACTION_TEXT}</p>}
        {groups.map((g) => (
          <div key={g.key} className="flex flex-col gap-1" data-testid={`pp-needs-${g.key}`}>
            <SectionLabel>{g.label} · {g.rows.length}{g.rows.length >= needs.limit ? "+" : ""}</SectionLabel>
            <ul className="flex flex-col">
              {g.rows.map((r) => (
                <li key={`${r.id}:${r.rev_no}:${r.step?.ordinal ?? ""}:${r.plan_review_id ?? ""}`}>
                  <button type="button" className="flex w-full items-baseline justify-between gap-3 rounded px-2 py-1.5 text-left text-[13px] hover:bg-bg-softer"
                    data-testid="pp-needs-row" onClick={() => onOpen(r.id)}>
                    <span className="min-w-0 truncate text-fg-primary">
                      <span className="op-mono text-[11.5px] text-fg-secondary">{r.plan_number} v{r.rev_no}</span> {r.title}
                    </span>
                    <span className="op-meta shrink-0">{g.what(r)}{r.since ? ` · ${formatUpdated(r.since)}` : ""}</span>
                  </button>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </section>
  );
}

/* ── all plans ──────────────────────────────────────────────────────────── */

// "See all in Plans" opens the plans list on the same tab: every key here is
// one the list's ?status= accepts (router.jsx PADPLANNER_LIST_TABS), "all" none.
function AllPlans({ counts, projectKey, onOpen, onSeeAll, className = "" }) {
  const [tab, setTab] = useState("all");
  const [planType, setPlanType] = useState("");
  const [owner, setOwner] = useState("");
  const [q, setQ] = useState("");
  const [qApplied, setQApplied] = useState("");
  const [page, setPage] = useState({ rows: [], cursor: null, error: null, loading: true });
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let cancelled = false;
    listPlans({ ...filtersFor({ tab, planType, owner, q: qApplied }), limit: PAGE })
      .then((r) => { if (!cancelled) setPage({ rows: r.plans, cursor: r.next_cursor, error: null, loading: false }); })
      .catch((e) => { if (!cancelled) setPage({ rows: [], cursor: null, error: e.message, loading: false }); });
    return () => { cancelled = true; };
  }, [tab, planType, owner, qApplied, projectKey, attempt]);

  function more() {
    setPage((p) => ({ ...p, loading: true }));
    listPlans({ ...filtersFor({ tab, planType, owner, q: qApplied }), limit: PAGE, cursor: page.cursor })
      .then((r) => setPage((p) => ({ rows: [...p.rows, ...r.plans], cursor: r.next_cursor, error: null, loading: false })))
      .catch((e) => setPage((p) => ({ ...p, error: e.message, loading: false })));
  }

  // "of M" only when M counts the same rows: no type, owner or search filter.
  const total = !planType && !owner && !qApplied ? countForTab(counts, tab) : null;

  return (
    <section className={`op-panel ${className}`} aria-labelledby="pp-all-head" data-testid="pp-all-plans">
      <div className="op-panel-head flex-wrap gap-3">
        <div className="flex items-baseline gap-3">
          <h2 id="pp-all-head" className="op-panel-title">All plans</h2>
          <button type="button" className="op-meta hover:text-fg-primary" data-testid="pp-see-all" onClick={() => onSeeAll(tab)}>See all in Plans</button>
        </div>
        <form className="flex flex-wrap items-center gap-2" onSubmit={(e) => { e.preventDefault(); setQApplied(q); }}>
          <input type="search" value={q} onChange={(e) => setQ(e.target.value)} aria-label="Filter plans"
            placeholder="Filter by number, title or API"
            className="w-60 rounded border border-bg-border bg-bg px-2 py-1 text-[12.5px] text-fg-primary placeholder:text-fg-secondary" />
          <select aria-label="Plan type" value={planType} onChange={(e) => setPlanType(e.target.value)}
            className="rounded border border-bg-border bg-bg px-2 py-1 text-[12.5px]">
            <option value="">All types</option>
            {PLAN_TYPES.map((t) => <option key={t} value={t}>{PLAN_TYPE_LABELS[t]}</option>)}
          </select>
          <select aria-label="Owner" value={owner} onChange={(e) => setOwner(e.target.value)}
            className="rounded border border-bg-border bg-bg px-2 py-1 text-[12.5px]">
            <option value="">Any owner</option>
            <option value="me">Owned by me</option>
          </select>
        </form>
      </div>
      <div className="flex flex-wrap gap-1 border-b border-bg-border px-3" role="tablist" aria-label="Plan status">
        {TABLE_TABS.map((t) => {
          const n = countForTab(counts, t.key);
          return (
            <button key={t.key} type="button" role="tab" aria-selected={tab === t.key} onClick={() => setTab(t.key)}
              className={`-mb-px border-b-2 px-3 py-1.5 text-[12.5px] ${tab === t.key ? "border-accent-action text-fg-primary" : "border-transparent text-fg-secondary hover:text-fg-primary"}`}>
              {t.label}{n !== null ? <span className="ml-1.5 op-mono text-fg-secondary">{n}</span> : null}
            </button>
          );
        })}
      </div>
      <div className="op-panel-body gap-2 overflow-y-auto" data-testid="pp-all-scroll">
        {/* A failed "Show more" retries that page; a failed first page reads it again. */}
        {page.error && <ReadFailed what="plans" error={page.error} testid="pp-plans-failed"
          onRetry={page.rows.length ? more : () => { setPage((p) => ({ ...p, error: null, loading: true })); setAttempt((n) => n + 1); }} />}
        {/* shrink-0: the body scrolls (the table and Showing N together), not this box. */}
        <div className="shrink-0 overflow-x-auto">
          <table className="w-full min-w-[760px] text-left text-[13px]">
            <thead className="op-label uppercase">
              <tr>
                <th className="py-1.5 pr-3 font-normal">Plan</th>
                <th className="py-1.5 pr-3 font-normal">Type</th>
                <th className="py-1.5 pr-3 font-normal">Status</th>
                <th className="py-1.5 pr-3 font-normal">Ver.</th>
                <th className="py-1.5 pr-3 font-normal">Target date</th>
                <th className="py-1.5 pr-3 font-normal">Imagery</th>
                <th className="py-1.5 font-normal">Updated</th>
              </tr>
            </thead>
            <tbody>
              {page.rows.map((p) => (
                <tr key={p.id} className="cursor-pointer border-t border-bg-border hover:bg-bg-softer" data-testid="pp-plan-row" onClick={() => onOpen(p.id)}>
                  <td className="py-2 pr-3">
                    <div className="text-fg-primary">{p.title} <span className="op-mono text-[11.5px] text-fg-secondary">{p.plan_number}</span></div>
                    <div className="op-meta">{PLAN_TYPE_LABELS[p.plan_type] ?? p.plan_type} · {p.well_count === 1 ? "1 well" : `${p.well_count} wells`}</div>
                  </td>
                  <td className="py-2 pr-3 text-fg-secondary">{PLAN_TYPE_LABELS[p.plan_type] ?? p.plan_type}</td>
                  <td className="py-2 pr-3"><StatusPill status={p.status} archived={Boolean(p.archived_at)} /></td>
                  <td className="py-2 pr-3 op-mono">v{p.current_revision}</td>
                  <td className="py-2 pr-3">{formatDate(p.target_date) ?? "—"}</td>
                  <td className="py-2 pr-3 text-fg-secondary">{p.imagery_dataset_key
                    ? imageryLabel({ sku_code: p.imagery_sku_code, resolution_cm: p.imagery_resolution_cm, acquisition_date: p.imagery_acquisition_date,
                      provenance: p.imagery_provenance })
                    : "Not pinned"}</td>
                  <td className="py-2 text-fg-secondary">{formatUpdated(p.updated_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {!page.loading && page.rows.length === 0 && !page.error && <p className="op-meta">No plans match.</p>}
        <div className="flex items-center justify-between gap-3">
          <span className="op-meta" data-testid="pp-showing">
            {page.rows.length > 0 ? `Showing ${page.rows.length}${total !== null ? ` of ${total}` : ""}` : ""}
          </span>
          {page.cursor && <button type="button" className="op-btn op-btn--sm" disabled={page.loading} onClick={more}>Show more</button>}
        </div>
      </div>
    </section>
  );
}
